/**
 * PATH: packages/quote-builder/src/grid/SheetGrid.tsx
 * FILE_ID: QB-GRID-SHEETGRID-001
 * PURPOSE: THE shared spreadsheet grid surface — an AG-Grid-backed
 *   editable grid with cell selection, keyboard nav, multi-cell paste
 *   from Excel (TSV/CSV), Ctrl/Cmd+D fill-down and a focus contract
 *   for an external formula bar.
 * WHY: ARC-3 (U06). Operator: the calculator "should be part of the
 *   main library of excel we are using in quote builder … and should
 *   expand that, not rebuild a separate new one". This file is the
 *   verbatim-in-spirit extraction of the quote-builder G8b AG-Grid
 *   editor's inner grid, generalised over column definitions so the
 *   quote workbook AND the kanban calculator render the same code.
 * FLOW:
 *   rows + columns → <AgGridReact> (lazy) → onChangeCell / onPasteRange /
 *   onFillDown / onFocusChange callbacks (persistence stays with caller)
 * DEPENDENCIES:
 *   - ag-grid-react / ag-grid-community (lazy dynamic import — bundle-safe)
 *   - ./sheetGridUpdates: pure paste / fill-down mapping
 */
import { Suspense, lazy, useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from 'react'

import { isComposingFormula, type RefInsertSpan } from './refInsert'
import type { SheetGridRow } from './sheetGridTypes'
import { useSheetGridFormulaCompose } from './useSheetGridFormulaCompose'

export type { SheetGridRow } from './sheetGridTypes'

export interface SheetGridColumn {
  field: string
  headerName: string
  width?: number
  minWidth?: number
  flex?: number
  editable?: boolean
  pinned?: 'left' | 'right'
}

export interface SheetGridProps {
  columns: SheetGridColumn[]
  rows: SheetGridRow[]
  /** Single-cell edit committed (typing in a cell). */
  onChangeCell: (rowId: string, field: string, value: string) => void
  /** Multi-cell Excel paste starting at the focused cell. */
  onPasteRange?: (startRowId: string, startField: string, cells: string[][]) => void
  /** Ctrl/Cmd+D fill-down across the selected range. */
  onFillDown?: (targetRowIds: string[], fields: string[], sourceRowId: string) => void
  /** Focus moved — feeds an external formula bar. */
  onFocusChange?: (row: SheetGridRow | null, field: string | null, value: string) => void
  /**
   * Optional display projection: what a NON-editing cell shows. The raw
   * value stays the edit value (AG-Grid hands the raw field value to the
   * editor). Used by the kanban calculator to show computed formula
   * results while keeping "=SUM(A1:A3)" as the editable text.
   */
  formatCellDisplay?: (row: SheetGridRow, field: string) => string
  /** Optional per-cell CSS class (e.g. red text on formula errors). */
  getCellClass?: (row: SheetGridRow, field: string) => string | undefined
  /**
   * Optional per-cell hover tooltip (native browser title). Used to
   * explain formula errors ("Missing closing bracket") on hover.
   */
  getCellTooltip?: (row: SheetGridRow, field: string) => string | undefined
  /**
   * Excel-style formula composition: when an input holding a "=…" formula
   * (the in-cell editor OR the paired formula bar) is focused, CLICKING a
   * grid cell inserts that cell's reference at the caret instead of moving
   * focus. Shift+click grows `A1:B3`; Ctrl/Cmd+click appends `,C4`.
   * Return the reference text for a cell (e.g. "B3"), or null for
   * non-referenceable cells. Omit to disable click-to-reference.
   */
  getCellA1Ref?: (row: SheetGridRow, field: string) => string | null
  /**
   * MR5 (planning layer) — Excel-style Shift+click range extension. When
   * wired, Shift+mousedown on a cell does NOT move focus (the anchor cell
   * stays active, like Excel); instead this fires with the clicked cell so
   * the host can extend its selection rectangle. Plain clicks still move
   * focus through onFocusChange.
   */
  onRangeExtend?: (row: SheetGridRow, field: string) => void
  /**
   * MR5 — per-cell editability veto (cell locks). Runs AFTER the column's
   * own `editable` flag; return false to block editing (AG never opens the
   * editor). Omit = every editable column stays editable.
   */
  isCellEditable?: (row: SheetGridRow, field: string) => boolean
  /** MR5 — per-row CSS class (header-row highlight etc.). */
  getRowClass?: (row: SheetGridRow) => string | undefined
  /**
   * MR5 — enable AG's built-in per-column text filter (header menu). The
   * filter matches what the cell DISPLAYS (computed value), not the raw
   * formula text, via formatCellDisplay when provided.
   */
  enableColumnFilters?: boolean
  /** Show the pinned 1-based row-number column (default true). */
  showRowNumbers?: boolean
  /** Excel-like visible gridlines between cells (default true). */
  showGridLines?: boolean
  rowHeight?: number
  headerHeight?: number
}

// ---------------------------------------------------------------------------
// AG-Grid dynamic import — one loader for every SheetGrid consumer.
// AG-Grid v32 ships a default module set when you pull in
// `ag-grid-community` itself — no registration is required for the
// features we use (ClientSideRowModel + community range / clipboard).
// ---------------------------------------------------------------------------

const LazyAgGridImpl = lazy(async () => {
  const [{ AgGridReact }] = await Promise.all([
    import('ag-grid-react'),
    import('ag-grid-community'),
    import('ag-grid-community/styles/ag-grid.css'),
    import('ag-grid-community/styles/ag-theme-quartz.css'),
  ])

  // MR4 (calc-arc mobile) — iOS Safari auto-zooms the whole page when a
  // focused input's computed font-size is <16px; AG's in-cell editor
  // inherits the theme's ~13px. One global, id-guarded rule (injected once
  // per document when the grid first loads) keeps phone editing zoom-free.
  const MOBILE_EDITOR_STYLE_ID = 'sheet-grid-mobile-editor-style'
  if (typeof document !== 'undefined' && !document.getElementById(MOBILE_EDITOR_STYLE_ID)) {
    const style = document.createElement('style')
    style.id = MOBILE_EDITOR_STYLE_ID
    style.textContent =
      '@media (max-width: 639px) { .ag-theme-quartz .ag-cell-inline-editing input { font-size: 16px; } }' +
      '.ag-theme-quartz .qb-sheet-compose-range { background-color: rgba(37, 99, 235, 0.18) !important; }'
    document.head.appendChild(style)
  }

  type ColDef = import('ag-grid-community').ColDef<SheetGridRow>
  type GridApi = import('ag-grid-community').GridApi<SheetGridRow>
  type CellValueChangedEvent =
    import('ag-grid-community').CellValueChangedEvent<SheetGridRow>
  type ProcessDataFromClipboardParams =
    import('ag-grid-community').ProcessDataFromClipboardParams<SheetGridRow>
  type CellKeyDownEvent = import('ag-grid-community').CellKeyDownEvent<SheetGridRow>
  type CellFocusedEvent = import('ag-grid-community').CellFocusedEvent<SheetGridRow>

  function Inner(props: SheetGridProps) {
    const {
      columns,
      rows,
      onChangeCell,
      onPasteRange,
      onFillDown,
      onFocusChange,
      formatCellDisplay,
      getCellClass,
      getCellTooltip,
      getCellA1Ref,
      onRangeExtend,
      isCellEditable,
      getRowClass,
      enableColumnFilters = false,
      showRowNumbers = true,
      showGridLines = true,
      rowHeight = 32,
      headerHeight = 36,
    } = props

    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const apiRef = useRef<GridApi | null>(null)
    // Latest rows/resolvers for the native listeners (avoids re-binding).
    const liveRef = useRef({ rows, getCellA1Ref, onRangeExtend, getCellClass })
    liveRef.current = { rows, getCellA1Ref, onRangeExtend, getCellClass }
    // Most recent programmatic ref insertion, so a second click REPLACES
    // the ref (Excel parity). Invalidated when the input value moved on.
    const lastInsertRef = useRef<{
      el: HTMLInputElement
      span: RefInsertSpan
      valueAfter: string
    } | null>(null)
    // Cells highlighted while composing a formula range (does NOT move the
    // formula-bar selection — that would drop the in-progress formula).
    const composeHighlightRef = useRef<Set<string>>(new Set())

    useSheetGridFormulaCompose({
      wrapperRef,
      apiRef,
      liveRef,
      lastInsertRef,
      composeHighlightRef,
    })

    // ── Shift+click → extend selection (Excel parity, MR5) ──────────────
    // Capture-phase so AG never moves focus to the clicked cell: the anchor
    // stays where it was and the HOST grows its selection rectangle.
    // During formula composition the formula-compose hook owns Shift+click
    // (range insert into the formula) — this path must not steal it.
    useEffect(() => {
      const wrapper = wrapperRef.current
      if (!wrapper) return

      const handleShiftMouseDown = (e: MouseEvent) => {
        const { rows: liveRows, onRangeExtend: extend } = liveRef.current
        if (!extend || !e.shiftKey) return
        // Formula composition wins over range extension.
        const active = document.activeElement
        if (active instanceof HTMLInputElement && isComposingFormula(active.value)) return

        const target = e.target instanceof Element ? e.target : null
        const cellEl = target?.closest('.ag-cell')
        if (!cellEl || !wrapper.contains(cellEl)) return
        const colId = cellEl.getAttribute('col-id')
        if (!colId || colId === '__rownum') return
        const rowId = cellEl.closest('[row-id]')?.getAttribute('row-id')
        if (!rowId) return
        const row = liveRows.find((r) => r.id === rowId)
        if (!row) return

        e.preventDefault()
        e.stopPropagation()
        extend(row, colId)
      }

      wrapper.addEventListener('mousedown', handleShiftMouseDown, true)
      return () => wrapper.removeEventListener('mousedown', handleShiftMouseDown, true)
    }, [])

    // NB: no useRef for the api — AG-Grid hands the api on every event;
    // keeping a ref invites stale-closure bugs across lazy remounts.
    const columnDefs = useMemo<ColDef[]>(() => {
      const defs: ColDef[] = []
      if (showRowNumbers) {
        defs.push({
          headerName: '#',
          colId: '__rownum',
          valueGetter: (p) => (p.node?.rowIndex ?? 0) + 1,
          width: 56,
          pinned: 'left',
          editable: false,
          suppressMovable: true,
        })
      }
      for (const col of columns) {
        defs.push({
          headerName: col.headerName,
          field: col.field,
          // MR5 — the per-cell veto (locks) runs after the column flag.
          editable:
            col.editable === false
              ? false
              : isCellEditable
                ? (p) => (p.data ? isCellEditable(p.data, col.field) : true)
                : true,
          width: col.width,
          minWidth: col.minWidth,
          flex: col.flex,
          pinned: col.pinned,
          // MR5 — header-menu text filter matches what the cell SHOWS
          // (computed value), not the raw "=SUM(…)" text.
          ...(enableColumnFilters
            ? {
                filter: 'agTextColumnFilter',
                ...(formatCellDisplay
                  ? {
                      filterValueGetter: (p: { data?: SheetGridRow }) =>
                        p.data ? formatCellDisplay(p.data, col.field) : '',
                    }
                  : {}),
              }
            : {}),
          ...(formatCellDisplay
            ? {
                valueFormatter: (p) =>
                  p.data ? formatCellDisplay(p.data, col.field) : '',
              }
            : {}),
          cellClass: (p) => {
            if (!p.data) return undefined
            const host = getCellClass?.(p.data, col.field)
            const a1 = getCellA1Ref?.(p.data, col.field)
            const compose =
              a1 && composeHighlightRef.current.has(a1.toUpperCase())
                ? 'qb-sheet-compose-range'
                : undefined
            const merged = [host, compose].filter(Boolean).join(' ')
            return merged || undefined
          },
          ...(getCellTooltip
            ? {
                tooltipValueGetter: (p: { data?: SheetGridRow }) =>
                  p.data ? (getCellTooltip(p.data, col.field) ?? null) : null,
              }
            : {}),
        })
      }
      return defs
    }, [
      columns,
      enableColumnFilters,
      formatCellDisplay,
      getCellA1Ref,
      getCellClass,
      getCellTooltip,
      isCellEditable,
      showRowNumbers,
    ])

    const editableFields = useMemo(
      () => columns.filter((c) => c.editable !== false).map((c) => c.field),
      [columns],
    )

    const handleCellValueChanged = (evt: CellValueChangedEvent) => {
      const field = evt.colDef.field
      if (!field || !evt.data) return
      const raw = evt.newValue
      onChangeCell(evt.data.id, field, raw == null ? '' : String(raw))
    }

    // Custom clipboard paste. AG-Grid Community's default paste only
    // writes to a single cell; to match Excel we split the clipboard
    // into TSV rows and dispatch a range write via onPasteRange.
    // Returning ``null`` tells AG-Grid to skip its own paste pipeline.
    const processDataFromClipboard = (params: ProcessDataFromClipboardParams) => {
      if (!onPasteRange) return params.data
      const api = params.api as GridApi | undefined
      if (!api) return params.data
      const focused = api.getFocusedCell()
      if (!focused) return params.data
      const field = focused.column.getColDef().field
      if (!field) return params.data
      const row = api.getDisplayedRowAtIndex(focused.rowIndex)
      if (!row?.data) return params.data
      onPasteRange(row.data.id, field, params.data)
      return null
    }

    const handleCellFocused = (evt: CellFocusedEvent) => {
      if (!onFocusChange) return
      const api = evt.api as GridApi
      const focused = api.getFocusedCell()
      if (!focused) {
        onFocusChange(null, null, '')
        return
      }
      const row = api.getDisplayedRowAtIndex(focused.rowIndex)
      const field = focused.column.getColDef().field
      if (!row?.data || !field) {
        onFocusChange(null, null, '')
        return
      }
      onFocusChange(row.data, field, String(row.data[field] ?? ''))
    }

    const handleCellKeyDown = (evt: CellKeyDownEvent) => {
      if (!onFillDown) return
      const kb = evt.event as KeyboardEvent | undefined
      if (!kb) return
      // Ctrl/Cmd + D → fill-down across the selected range.
      if ((kb.ctrlKey || kb.metaKey) && (kb.key === 'd' || kb.key === 'D')) {
        kb.preventDefault()
        const api = evt.api as GridApi
        const ranges = api.getCellRanges?.() || []
        if (ranges.length === 0) return
        const range = ranges[0]
        const startIdx = Math.min(
          range.startRow?.rowIndex ?? 0,
          range.endRow?.rowIndex ?? 0,
        )
        const endIdx = Math.max(
          range.startRow?.rowIndex ?? 0,
          range.endRow?.rowIndex ?? 0,
        )
        const fields = range.columns
          .map((c) => c.getColDef().field)
          .filter((f): f is string => !!f && editableFields.includes(f))
        if (fields.length === 0) return
        const sourceRow = api.getDisplayedRowAtIndex(startIdx)?.data
        if (!sourceRow) return
        const targetRowIds: string[] = []
        for (let i = startIdx + 1; i <= endIdx; i++) {
          const r = api.getDisplayedRowAtIndex(i)?.data
          if (r) targetRowIds.push(r.id)
        }
        if (targetRowIds.length === 0) return
        onFillDown(targetRowIds, fields, sourceRow.id)
      }
    }

    // Quartz ships with NO vertical cell borders and very faint row lines —
    // a spreadsheet needs visible gridlines (client: "can't see borders").
    // The focused-cell border is likewise near-invisible by default
    // (client: "cell highlight and selection is a bit clunky") — quartz
    // draws it with --ag-range-selection-border-color, so pin it to a
    // solid Excel-like accent.
    const gridLineStyle: CSSProperties = {
      '--ag-range-selection-border-color': '#2563eb',
      ...(showGridLines
        ? {
            '--ag-cell-horizontal-border': 'solid var(--ag-border-color)',
            '--ag-row-border-color': 'var(--ag-border-color)',
            '--ag-header-column-separator-display': 'block',
            '--ag-header-column-separator-color': 'var(--ag-border-color)',
          }
        : {}),
    } as CSSProperties

    return (
      <div
        ref={wrapperRef}
        className="ag-theme-quartz"
        style={{ width: '100%', height: '100%', ...gridLineStyle }}
      >
        <AgGridReact<SheetGridRow>
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: false,
            resizable: true,
            suppressMovable: false,
          }}
          onGridReady={(e) => {
            apiRef.current = e.api
          }}
          onCellValueChanged={handleCellValueChanged}
          onCellFocused={handleCellFocused}
          onCellKeyDown={handleCellKeyDown}
          processDataFromClipboard={processDataFromClipboard}
          // Native browser titles: zero-dependency tooltips for error cells.
          enableBrowserTooltips={true}
          enableRangeSelection={true}
          suppressCopyRowsToClipboard={true}
          animateRows={false}
          singleClickEdit={false}
          stopEditingWhenCellsLoseFocus={true}
          // Excel key parity (client: "enter logic and key structures are
          // a bit clunky"): Enter moves the focus DOWN one cell — both
          // when navigating and after committing an edit — instead of
          // AG's default stay-in-place.
          enterNavigatesVertically={true}
          enterNavigatesVerticallyAfterEdit={true}
          // MR5 — per-row class (header-row highlight from the host).
          {...(getRowClass
            ? { getRowClass: (p: { data?: SheetGridRow }) => (p.data ? getRowClass(p.data) : undefined) }
            : {})}
          getRowId={(p) => p.data.id}
          rowHeight={rowHeight}
          headerHeight={headerHeight}
        />
      </div>
    )
  }

  return { default: Inner }
})

/**
 * Public component. Lazy-loads AG-Grid on first render so consumers that
 * never open a grid (most kanban boards, most pages) pay zero bundle cost.
 */
export function SheetGrid(props: SheetGridProps & { loadingFallback?: ReactNode }) {
  const { loadingFallback, ...gridProps } = props
  return (
    <Suspense
      fallback={
        loadingFallback ?? (
          <div
            style={{
              display: 'flex',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Loading grid…
          </div>
        )
      }
    >
      <LazyAgGridImpl {...gridProps} />
    </Suspense>
  )
}
